import React from 'react';

import cn from 'classnames';

import { string, shape, bool } from 'prop-types';
import Markdown from 'react-markdown';

import Text from 'components/Common/Text';
import TextLink from 'components/Common/TextLink';

import { ensureArray } from 'utils/arrays';

import Speaker from 'modules/ElementaryCareers/components/Speaker';

import s from './RenderMarkdown.css';

const defaultComponents = {
  h1: ({ node, ...props }) => <Text variant="h5" {...props} />,
  h2: ({ node, ...props }) => <Text variant="leadBold" {...props} />,
  p: ({ node, ...props }) => <Text variant="bodyMd" {...props} />,
  a: ({ node, ...props }) => <TextLink {...props} />,
};

const extractTextFromNode = children =>
  React.Children.toArray(children).join('');

const ttsComponents = {
  h1: ({ node, ...props }) => (
    <Speaker dataTtsId={extractTextFromNode(props.children)}>
      <Text variant="h5" {...props} />
    </Speaker>
  ),
  h2: ({ node, ...props }) => (
    <Speaker dataTtsId={extractTextFromNode(props.children)}>
      <Text variant="leadBold" {...props} />
    </Speaker>
  ),
  h3: ({ node, ...props }) => (
    <Speaker dataTtsId={extractTextFromNode(props.children)}>
      <Text variant="h3" {...props} />
    </Speaker>
  ),
  h4: ({ node, ...props }) => (
    <Speaker dataTtsId={extractTextFromNode(props.children)}>
      <Text variant="h4" {...props} />
    </Speaker>
  ),
  h5: ({ node, ...props }) => (
    <Speaker dataTtsId={extractTextFromNode(props.children)}>
      <Text variant="h5" {...props} />
    </Speaker>
  ),
  p: ({ node, ...props }) => {
    const hasHtmlElements = ensureArray(props.children).some(child =>
      React.isValidElement(child),
    );

    // if p tag has other nested html elements, we don't wrap it with speaker
    // we will wrap the children
    if (hasHtmlElements) {
      return <Text variant="bodyMd" {...props} />;
    }

    return (
      <Speaker dataTtsId={extractTextFromNode(props.children)}>
        <Text variant="bodyMd" {...props} />
      </Speaker>
    );
  },
  b: ({ node, ...props }) => (
    <Speaker dataTtsId={extractTextFromNode(props.children)}>
      <Text variant="bodyMd" isBold {...props} />
    </Speaker>
  ),
  em: ({ node, ...props }) => (
    <Speaker dataTtsId={extractTextFromNode(props.children)}>
      <em {...props} />
    </Speaker>
  ),
  span: ({ node, ...props }) => (
    <Speaker dataTtsId={extractTextFromNode(props.children)}>
      <Text variant="bodySm" {...props} />
    </Speaker>
  ),
  li: ({ node, ...props }) => (
    <Speaker dataTtsId={extractTextFromNode(props.children)}>
      <li {...props} />
    </Speaker>
  ),
};

const googleTranslateComponents = {
  font: ({ node, ...props }) => (
    <Speaker dataTtsId={extractTextFromNode(props.children)}>
      <Text variant="bodySm" {...props} />
    </Speaker>
  ),
};

const RenderMarkdown = ({
  children,
  renderers,
  className,
  useTts,
  useGoogleTranslate,
  // components, rehypePlugins, allowedElements and other react-markdown props(https://github.com/remarkjs/react-markdown#props) can be passed here
  ...markdownRestProps
}) => {
  if (!children) {
    return null;
  }

  const getComponents = () => {
    if (useTts) {
      return ttsComponents;
    }
    if (useGoogleTranslate) {
      return googleTranslateComponents;
    }
    return defaultComponents;
  };

  return (
    <Markdown
      renderers={renderers}
      className={cn(s.markdownContainer, className)}
      components={getComponents()}
      {...markdownRestProps}
    >
      {children}
    </Markdown>
  );
};

RenderMarkdown.propTypes = {
  children: string.isRequired,
  renderers: shape({}),
  className: string,
  useTts: bool,
  useGoogleTranslate: bool,
};

RenderMarkdown.defaultProps = {
  renderers: undefined,
  className: null,
  useTts: false,
  useGoogleTranslate: false,
};

export default RenderMarkdown;
